import { type CSSProperties, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useAuth } from '@helios/auth-firebase-web';
import { Button } from '@helios/ui/button';
import { Modal } from '@helios/ui/overlay';
import {
  qAppConfig,
  getRegionalSettings,
  formatMoney,
  writeRegionalSettings,
  PH_DEFAULT_REGIONAL_SETTINGS,
  // PRD.282 — clinic classification + workspace preset
  qPatientWorkspaceSettings,
  mapPatientWorkspaceSettingsToUI,
  PATIENT_WORKSPACE_DEFAULTS,
  getClinicClassification,
  writeClinicClassification,
  normalizeClinicClassification,
  buildClinicWorkspacePreset,
  applyPresetToWorkspaceSettings,
  getImpliedSpecialties,
  PRIMARY_CLINIC_TYPES,
  CLINIC_SPECIALTIES,
  PRIMARY_CLINIC_TYPE_LABELS,
  CLINIC_SPECIALTY_LABELS,
  // PRD.283 — clinic main-module config (extra_json.main_modules)
  isInvoiceModuleEnabled,
  isLaboratoryModuleEnabled,
  writeMainModules,
  // PRD.284 — print branding (extra_json.print_branding)
  getPrintBranding,
  writePrintBranding,
  normalizePrintFontColor,
  PRINT_BRANDING_DEFAULTS,
  type PrintBrandingConfig,
  type AppConfigRow,
  type RegionalSettings,
  type IdTypeEntry,
  type MoneyCurrencyDisplay,
  type MoneyCurrencyPosition,
  type PatientWorkspaceSettingsRow,
  type PatientWorkspaceSettingsUI,
  type PrimaryClinicType,
  type ClinicSpecialty,
} from '@helios/data-access';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import { persistPatientWorkspaceSettings } from '../clinic/persistPatientWorkspaceSettings';

// ─── Styles ───

const pageStyle: CSSProperties = {
  maxWidth: 760,
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-6, 24px)',
};

const sectionStyle: CSSProperties = {
  padding: 'var(--sp-5, 20px)',
  background: 'var(--bg-surface, #fff)',
  border: '1px solid var(--border, rgba(15,23,42,0.12))',
  borderRadius: 'var(--radius-lg, 14px)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-4, 16px)',
};

const sectionTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 'var(--font-weight-semi, 600)',
  color: 'var(--text, #0f172a)',
  margin: 0,
};

const labelStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 14px)',
  color: 'var(--text-muted, #64748b)',
  marginBottom: 4,
};

// PRD.283 — Main Modules displayed in Clinic Settings. Labels mirror the app's
// real navigation modules (see AppLauncher). Only `invoice` is editable in this
// phase; every other module is locked-on and persists no clinic override. Add a
// row here and mark it `configurable` to make a future module editable.
type MainModuleRow = {
  key: string;
  label: string;
  description?: string;
  configurable: boolean;
};
const MAIN_MODULE_ROWS: readonly MainModuleRow[] = [
  { key: 'patients', label: 'Patients', configurable: false },
  { key: 'appointments', label: 'Appointments', configurable: false },
  { key: 'queue', label: 'Queue', configurable: false },
  {
    key: 'invoice',
    label: 'Invoice',
    description: 'Billing on consultations and the Pay action in Queue.',
    configurable: true,
  },
  { key: 'inventory', label: 'Inventory', configurable: false },
  { key: 'pharmacy', label: 'Pharmacy', configurable: false },
  { key: 'packages', label: 'Patient Packages', configurable: false },
  {
    // PRD.305 — the dedicated Laboratory module. Disabling it hides the
    // Laboratory navigation, route and every operational action; previously
    // published results stay readable in Patient Workspace and Files.
    key: 'laboratory',
    label: 'Laboratory',
    description: 'Lab orders, processing and result publication.',
    configurable: true,
  },
  { key: 'elia_ai', label: 'Elia AI', configurable: false },
];

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  fontSize: 'var(--font-size-sm, 14px)',
  borderRadius: 'var(--radius-md, 10px)',
  border: '1px solid var(--border, rgba(15,23,42,0.12))',
  outline: 'none',
  color: 'var(--text, #0f172a)',
  background: '#fff',
  boxSizing: 'border-box',
};

const inputReadOnly: CSSProperties = {
  ...inputStyle,
  background: '#f8fafc',
  color: '#64748b',
};

const btnStyle: CSSProperties = {
  padding: '8px 16px',
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 'var(--font-weight-medium, 500)',
  borderRadius: 'var(--radius-md, 10px)',
  border: '1px solid var(--border, rgba(15,23,42,0.12))',
  background: '#fff',
  color: 'var(--text, #0f172a)',
  cursor: 'pointer',
};

const btnPrimaryStyle: CSSProperties = {
  ...btnStyle,
  background: 'var(--color-primary, #2563eb)',
  color: '#fff',
  border: 'none',
};

const btnDangerLinkStyle: CSSProperties = {
  ...btnStyle,
  background: 'transparent',
  border: 'none',
  color: '#dc2626',
  padding: '4px 8px',
};

const twoColRow: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 'var(--sp-4, 16px)',
};

const idTypeRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--sp-3, 12px)',
  padding: '8px 10px',
  borderRadius: 'var(--radius-md, 10px)',
  border: '1px solid var(--border, rgba(15,23,42,0.12))',
  background: '#fff',
};

// ─── Country / Currency presets ───

interface CountryPreset {
  code: string;
  label: string;
  region: string;
  currency: string;
  currency_symbol: string;
  mobile_cc: string;
}

const COUNTRY_PRESETS: CountryPreset[] = [
  {
    code: 'PH',
    label: 'Philippines',
    region: 'Philippines',
    currency: 'PHP',
    currency_symbol: '₱',
    mobile_cc: '+63',
  },
  {
    code: 'US',
    label: 'United States',
    region: 'United States',
    currency: 'USD',
    currency_symbol: '$',
    mobile_cc: '+1',
  },
  {
    code: 'CA',
    label: 'Canada',
    region: 'Canada',
    currency: 'CAD',
    currency_symbol: '$',
    mobile_cc: '+1',
  },
  {
    code: 'GB',
    label: 'United Kingdom',
    region: 'United Kingdom',
    currency: 'GBP',
    currency_symbol: '£',
    mobile_cc: '+44',
  },
  {
    code: 'AU',
    label: 'Australia',
    region: 'Australia',
    currency: 'AUD',
    currency_symbol: '$',
    mobile_cc: '+61',
  },
  {
    code: 'NZ',
    label: 'New Zealand',
    region: 'New Zealand',
    currency: 'NZD',
    currency_symbol: '$',
    mobile_cc: '+64',
  },
  {
    code: 'SG',
    label: 'Singapore',
    region: 'Singapore',
    currency: 'SGD',
    currency_symbol: '$',
    mobile_cc: '+65',
  },
  {
    code: 'MY',
    label: 'Malaysia',
    region: 'Malaysia',
    currency: 'MYR',
    currency_symbol: 'RM',
    mobile_cc: '+60',
  },
  {
    code: 'ID',
    label: 'Indonesia',
    region: 'Indonesia',
    currency: 'IDR',
    currency_symbol: 'Rp',
    mobile_cc: '+62',
  },
  {
    code: 'TH',
    label: 'Thailand',
    region: 'Thailand',
    currency: 'THB',
    currency_symbol: '฿',
    mobile_cc: '+66',
  },
  {
    code: 'VN',
    label: 'Vietnam',
    region: 'Vietnam',
    currency: 'VND',
    currency_symbol: '₫',
    mobile_cc: '+84',
  },
  {
    code: 'HK',
    label: 'Hong Kong',
    region: 'Hong Kong',
    currency: 'HKD',
    currency_symbol: '$',
    mobile_cc: '+852',
  },
  {
    code: 'TW',
    label: 'Taiwan',
    region: 'Taiwan',
    currency: 'TWD',
    currency_symbol: 'NT$',
    mobile_cc: '+886',
  },
  {
    code: 'JP',
    label: 'Japan',
    region: 'Japan',
    currency: 'JPY',
    currency_symbol: '¥',
    mobile_cc: '+81',
  },
  {
    code: 'KR',
    label: 'South Korea',
    region: 'South Korea',
    currency: 'KRW',
    currency_symbol: '₩',
    mobile_cc: '+82',
  },
  {
    code: 'CN',
    label: 'China',
    region: 'China',
    currency: 'CNY',
    currency_symbol: '¥',
    mobile_cc: '+86',
  },
  {
    code: 'IN',
    label: 'India',
    region: 'India',
    currency: 'INR',
    currency_symbol: '₹',
    mobile_cc: '+91',
  },
  {
    code: 'AE',
    label: 'United Arab Emirates',
    region: 'United Arab Emirates',
    currency: 'AED',
    currency_symbol: 'د.إ',
    mobile_cc: '+971',
  },
  {
    code: 'SA',
    label: 'Saudi Arabia',
    region: 'Saudi Arabia',
    currency: 'SAR',
    currency_symbol: 'ر.س',
    mobile_cc: '+966',
  },
  {
    code: 'ZA',
    label: 'South Africa',
    region: 'South Africa',
    currency: 'ZAR',
    currency_symbol: 'R',
    mobile_cc: '+27',
  },
  {
    code: 'DE',
    label: 'Germany',
    region: 'Germany',
    currency: 'EUR',
    currency_symbol: '€',
    mobile_cc: '+49',
  },
  {
    code: 'FR',
    label: 'France',
    region: 'France',
    currency: 'EUR',
    currency_symbol: '€',
    mobile_cc: '+33',
  },
  {
    code: 'ES',
    label: 'Spain',
    region: 'Spain',
    currency: 'EUR',
    currency_symbol: '€',
    mobile_cc: '+34',
  },
  {
    code: 'IT',
    label: 'Italy',
    region: 'Italy',
    currency: 'EUR',
    currency_symbol: '€',
    mobile_cc: '+39',
  },
  {
    code: 'NL',
    label: 'Netherlands',
    region: 'Netherlands',
    currency: 'EUR',
    currency_symbol: '€',
    mobile_cc: '+31',
  },
];

interface CurrencyOption {
  code: string;
  symbol: string;
  label: string;
  /** Conventional ISO 4217 minor unit count for this currency. */
  decimal_places: number;
}

// Per-country ID type presets. Curated from each country's most common
// government-issued IDs; not exhaustive — clinics can disable/edit/add.
// PH inherits from PH_DEFAULT_REGIONAL_SETTINGS so there is one source of
// truth for the Philippines.
const COUNTRY_ID_TYPES: Record<string, IdTypeEntry[]> = {
  PH: PH_DEFAULT_REGIONAL_SETTINGS.id_types.map((t) => ({ ...t })),
  US: [
    { code: 'ssn', label: 'Social Security Number (SSN)', enabled: true },
    { code: 'drivers_license', label: "Driver's License", enabled: true },
    { code: 'state_id', label: 'State ID', enabled: true },
    { code: 'passport', label: 'Passport', enabled: true },
    { code: 'medicare', label: 'Medicare Card', enabled: true },
    { code: 'medicaid', label: 'Medicaid Card', enabled: true },
    { code: 'military_id', label: 'Military ID', enabled: true },
    { code: 'green_card', label: 'Permanent Resident Card', enabled: true },
  ],
  CA: [
    { code: 'health_card', label: 'Provincial Health Card', enabled: true },
    { code: 'drivers_license', label: "Driver's Licence", enabled: true },
    { code: 'passport', label: 'Passport', enabled: true },
    { code: 'sin', label: 'Social Insurance Number (SIN)', enabled: true },
    { code: 'pr_card', label: 'Permanent Resident Card', enabled: true },
  ],
  GB: [
    { code: 'passport', label: 'Passport', enabled: true },
    { code: 'drivers_license', label: 'Driving Licence', enabled: true },
    { code: 'nhs_number', label: 'NHS Number', enabled: true },
    { code: 'ni_number', label: 'National Insurance Number', enabled: true },
    { code: 'brp', label: 'Biometric Residence Permit (BRP)', enabled: true },
  ],
  AU: [
    { code: 'medicare', label: 'Medicare Card', enabled: true },
    { code: 'drivers_license', label: "Driver's Licence", enabled: true },
    { code: 'passport', label: 'Passport', enabled: true },
    { code: 'proof_of_age', label: 'Proof of Age Card', enabled: true },
  ],
  NZ: [
    { code: 'drivers_license', label: 'Driver Licence', enabled: true },
    { code: 'passport', label: 'Passport', enabled: true },
    { code: 'nhi', label: 'NHI Number', enabled: true },
    { code: 'kiwi_access', label: 'Kiwi Access Card', enabled: true },
  ],
  SG: [
    { code: 'nric', label: 'NRIC', enabled: true },
    { code: 'fin', label: 'FIN (Foreign ID)', enabled: true },
    { code: 'passport', label: 'Passport', enabled: true },
    { code: 'drivers_license', label: "Driver's Licence", enabled: true },
  ],
  MY: [
    { code: 'mykad', label: 'MyKad (NRIC)', enabled: true },
    { code: 'passport', label: 'Passport', enabled: true },
    { code: 'drivers_license', label: "Driver's Licence", enabled: true },
    { code: 'mykid', label: 'MyKid', enabled: true },
    { code: 'mytentera', label: 'MyTentera (Military)', enabled: true },
  ],
  ID: [
    { code: 'ktp', label: 'KTP (National ID)', enabled: true },
    { code: 'kitas', label: 'KITAS (Stay Permit)', enabled: true },
    { code: 'passport', label: 'Passport', enabled: true },
    { code: 'sim', label: 'SIM (Driving Licence)', enabled: true },
    { code: 'npwp', label: 'NPWP (Tax ID)', enabled: true },
    { code: 'bpjs', label: 'BPJS Kesehatan', enabled: true },
  ],
  TH: [
    { code: 'national_id', label: 'Thai National ID Card', enabled: true },
    { code: 'passport', label: 'Passport', enabled: true },
    { code: 'drivers_license', label: "Driver's Licence", enabled: true },
    { code: 'work_permit', label: 'Work Permit', enabled: true },
  ],
  VN: [
    { code: 'citizen_id', label: 'Citizen ID Card (CCCD)', enabled: true },
    { code: 'national_id', label: 'National ID Card (CMND)', enabled: true },
    { code: 'passport', label: 'Passport', enabled: true },
    { code: 'drivers_license', label: "Driver's Licence", enabled: true },
    { code: 'health_insurance', label: 'Health Insurance Card', enabled: true },
  ],
  HK: [
    { code: 'hkid', label: 'HKID', enabled: true },
    { code: 'passport', label: 'Passport', enabled: true },
    { code: 'drivers_license', label: "Driver's Licence", enabled: true },
  ],
  TW: [
    { code: 'national_id', label: 'National ID Card', enabled: true },
    { code: 'arc', label: 'Alien Resident Certificate (ARC)', enabled: true },
    { code: 'passport', label: 'Passport', enabled: true },
    { code: 'drivers_license', label: "Driver's License", enabled: true },
    { code: 'nhi', label: 'NHI Card', enabled: true },
  ],
  JP: [
    { code: 'my_number', label: 'My Number Card', enabled: true },
    { code: 'health_insurance', label: 'Health Insurance Card', enabled: true },
    { code: 'passport', label: 'Passport', enabled: true },
    { code: 'drivers_license', label: "Driver's License", enabled: true },
    { code: 'residence_card', label: 'Residence Card', enabled: true },
  ],
  KR: [
    { code: 'rrc', label: 'Resident Registration Card', enabled: true },
    { code: 'arc', label: 'Alien Registration Card', enabled: true },
    { code: 'passport', label: 'Passport', enabled: true },
    { code: 'drivers_license', label: "Driver's License", enabled: true },
  ],
  CN: [
    { code: 'resident_id', label: 'Resident Identity Card', enabled: true },
    { code: 'passport', label: 'Passport', enabled: true },
    { code: 'drivers_license', label: "Driver's License", enabled: true },
    { code: 'hukou', label: 'Hukou Booklet', enabled: true },
  ],
  IN: [
    // 'aadhaar' intentionally omitted — withheld for legal reasons
    // (see BLOCKED_ID_TYPE_KEYS in app-config.ts).
    { code: 'pan', label: 'PAN Card', enabled: true },
    { code: 'passport', label: 'Passport', enabled: true },
    { code: 'drivers_license', label: "Driver's License", enabled: true },
    { code: 'voter_id', label: 'Voter ID', enabled: true },
    { code: 'ration_card', label: 'Ration Card', enabled: true },
    { code: 'abha', label: 'ABHA (Health ID)', enabled: true },
  ],
  AE: [
    { code: 'emirates_id', label: 'Emirates ID', enabled: true },
    { code: 'passport', label: 'Passport', enabled: true },
    { code: 'drivers_license', label: "Driver's License", enabled: true },
    { code: 'residence_visa', label: 'Residence Visa', enabled: true },
  ],
  SA: [
    { code: 'national_id', label: 'National ID', enabled: true },
    { code: 'iqama', label: 'Iqama', enabled: true },
    { code: 'passport', label: 'Passport', enabled: true },
    { code: 'drivers_license', label: "Driver's License", enabled: true },
  ],
  ZA: [
    { code: 'national_id', label: 'South African ID', enabled: true },
    { code: 'passport', label: 'Passport', enabled: true },
    { code: 'drivers_license', label: "Driver's License", enabled: true },
  ],
  DE: [
    { code: 'personalausweis', label: 'Personalausweis (ID Card)', enabled: true },
    { code: 'passport', label: 'Reisepass (Passport)', enabled: true },
    { code: 'drivers_license', label: 'Führerschein (Driving Licence)', enabled: true },
    { code: 'health_insurance', label: 'Krankenversichertenkarte', enabled: true },
  ],
  FR: [
    { code: 'cni', label: "Carte Nationale d'Identité", enabled: true },
    { code: 'passport', label: 'Passport', enabled: true },
    { code: 'drivers_license', label: 'Permis de Conduire', enabled: true },
    { code: 'carte_vitale', label: 'Carte Vitale', enabled: true },
  ],
  ES: [
    { code: 'dni', label: 'DNI', enabled: true },
    { code: 'nie', label: 'NIE', enabled: true },
    { code: 'passport', label: 'Pasaporte', enabled: true },
    { code: 'drivers_license', label: 'Permiso de Conducir', enabled: true },
    { code: 'health_card', label: 'Tarjeta Sanitaria', enabled: true },
  ],
  IT: [
    { code: 'carta_identita', label: "Carta d'Identità", enabled: true },
    { code: 'codice_fiscale', label: 'Codice Fiscale', enabled: true },
    { code: 'passport', label: 'Passaporto', enabled: true },
    { code: 'drivers_license', label: 'Patente di Guida', enabled: true },
    { code: 'tessera_sanitaria', label: 'Tessera Sanitaria', enabled: true },
  ],
  NL: [
    { code: 'national_id', label: 'Dutch ID Card', enabled: true },
    { code: 'passport', label: 'Passport', enabled: true },
    { code: 'drivers_license', label: 'Rijbewijs', enabled: true },
    { code: 'bsn', label: 'BSN (Citizen Service Number)', enabled: true },
  ],
};

const CURRENCY_OPTIONS: CurrencyOption[] = [
  { code: 'PHP', symbol: '₱', label: 'Philippine Peso', decimal_places: 2 },
  { code: 'USD', symbol: '$', label: 'US Dollar', decimal_places: 2 },
  { code: 'EUR', symbol: '€', label: 'Euro', decimal_places: 2 },
  { code: 'GBP', symbol: '£', label: 'British Pound', decimal_places: 2 },
  { code: 'CAD', symbol: '$', label: 'Canadian Dollar', decimal_places: 2 },
  { code: 'AUD', symbol: '$', label: 'Australian Dollar', decimal_places: 2 },
  { code: 'NZD', symbol: '$', label: 'New Zealand Dollar', decimal_places: 2 },
  { code: 'SGD', symbol: '$', label: 'Singapore Dollar', decimal_places: 2 },
  { code: 'MYR', symbol: 'RM', label: 'Malaysian Ringgit', decimal_places: 2 },
  { code: 'IDR', symbol: 'Rp', label: 'Indonesian Rupiah', decimal_places: 0 },
  { code: 'THB', symbol: '฿', label: 'Thai Baht', decimal_places: 2 },
  { code: 'VND', symbol: '₫', label: 'Vietnamese Dong', decimal_places: 0 },
  { code: 'HKD', symbol: '$', label: 'Hong Kong Dollar', decimal_places: 2 },
  { code: 'TWD', symbol: 'NT$', label: 'Taiwan Dollar', decimal_places: 2 },
  { code: 'JPY', symbol: '¥', label: 'Japanese Yen', decimal_places: 0 },
  { code: 'KRW', symbol: '₩', label: 'South Korean Won', decimal_places: 0 },
  { code: 'CNY', symbol: '¥', label: 'Chinese Yuan', decimal_places: 2 },
  { code: 'INR', symbol: '₹', label: 'Indian Rupee', decimal_places: 2 },
  { code: 'AED', symbol: 'د.إ', label: 'UAE Dirham', decimal_places: 2 },
  { code: 'SAR', symbol: 'ر.س', label: 'Saudi Riyal', decimal_places: 2 },
  { code: 'ZAR', symbol: 'R', label: 'South African Rand', decimal_places: 2 },
];

// ─── Helpers ───

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_RE = /^(https?:)?\/\/[^\s]+$/i;

function toSnakeCode(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);
}

function uniqueCode(base: string, existing: Set<string>): string {
  const fallback = base || 'custom_id';
  if (!existing.has(fallback)) return fallback;
  let i = 2;
  while (existing.has(`${fallback}_${i}`)) i++;
  return `${fallback}_${i}`;
}

// ─── Component ───

export function ClinicSettingsPage() {
  const { user, claims } = useAuth();
  const { db } = usePowerSyncDb();

  const orgId = claims?.activeOrg ?? '';
  const roles = orgId ? (claims?.orgs?.[orgId]?.roles ?? []) : [];
  const isAdmin = roles.includes('admin');
  const userId = user?.uid ?? null;

  const { data: configRows } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const row = configRows?.[0] ?? null;

  // ─── Form state (hydrated from the synced row; see effect below) ───
  // `dirty` = the user has unsaved edits, so we must NOT overwrite their input
  // when the watch query emits.
  const [dirty, setDirty] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [orgLocalname, setOrgLocalname] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [orgAddress, setOrgAddress] = useState('');
  const [orgPhone, setOrgPhone] = useState('');
  const [orgMobile, setOrgMobile] = useState('');
  const [orgEmail, setOrgEmail] = useState('');
  const [country, setCountry] = useState('PH');
  const [region, setRegion] = useState('Philippines');
  const [language, setLanguage] = useState('en');
  const [currency, setCurrency] = useState('PHP');
  const [smsSenderName, setSmsSenderName] = useState('');
  const [regional, setRegional] = useState<RegionalSettings>(() => ({
    ...PH_DEFAULT_REGIONAL_SETTINGS,
    money_format: { ...PH_DEFAULT_REGIONAL_SETTINGS.money_format },
    id_types: PH_DEFAULT_REGIONAL_SETTINGS.id_types.map((t) => ({ ...t })),
  }));

  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ text: string; tone: 'success' | 'error' } | null>(null);
  const [newIdLabel, setNewIdLabel] = useState('');

  // PRD.283 — Invoice main-module toggle (extra_json.main_modules.invoice).
  // Defaults to enabled; only an explicit stored `false` disables it.
  const [invoiceModuleEnabled, setInvoiceModuleEnabled] = useState(true);
  // PRD.305 — Laboratory is the second configurable main module.
  const [laboratoryModuleEnabled, setLaboratoryModuleEnabled] = useState(true);

  /**
   * Editable main modules, keyed the same way `MAIN_MODULE_ROWS` is. Rows
   * without an entry render locked-on and persist no override, which is how
   * every not-yet-configurable module behaves.
   */
  const MODULE_STATE: Record<string, { value: boolean; set: (v: boolean) => void } | undefined> = {
    invoice: { value: invoiceModuleEnabled, set: setInvoiceModuleEnabled },
    laboratory: { value: laboratoryModuleEnabled, set: setLaboratoryModuleEnabled },
  };

  // PRD.284 — print branding form state (extra_json.print_branding).
  const [printBranding, setPrintBranding] = useState<PrintBrandingConfig>(() => ({
    ...PRINT_BRANDING_DEFAULTS,
  }));
  const patchPrintBranding = useCallback((patch: Partial<PrintBrandingConfig>) => {
    setDirty(true);
    setPrintBranding((prev) => ({ ...prev, ...patch }));
  }, []);

  // PRD.282 — clinic classification form state (stored as codes in extra_json).
  const [primaryClinicType, setPrimaryClinicType] = useState<PrimaryClinicType>('general');
  const [additionalSpecialties, setAdditionalSpecialties] = useState<ClinicSpecialty[]>([]);
  // Confirmation dialog when changing the type of an already-configured clinic.
  const [clinicDefaultsConfirmOpen, setClinicDefaultsConfirmOpen] = useState(false);

  // The org-default Patient Workspace row tells us whether workspace settings
  // already exist (existing clinic → confirm before replacing; new clinic →
  // auto-apply the preset). Read-only here — we write through the shared path.
  const { data: workspaceRows = [] } = usePowerSyncQuery<PatientWorkspaceSettingsRow>(
    qPatientWorkspaceSettings(),
  );

  const savedClassification = useMemo(() => getClinicClassification(row), [row]);
  const formClassification = useMemo(
    () => normalizeClinicClassification({ primaryClinicType, additionalSpecialties }),
    [primaryClinicType, additionalSpecialties],
  );
  const classificationChanged = useMemo(
    () => JSON.stringify(formClassification) !== JSON.stringify(savedClassification),
    [formClassification, savedClassification],
  );
  // Specialties the primary type already implies are hidden from the picker.
  const availableSpecialties = useMemo(() => {
    const implied = new Set(getImpliedSpecialties(primaryClinicType));
    return CLINIC_SPECIALTIES.filter((s) => !implied.has(s));
  }, [primaryClinicType]);

  // Hydrate the form from the synced row. `usePowerSyncQuery` is a live
  // `db.watch()` query, so the row can change after mount (first load, or
  // another admin's edit syncing in). We re-hydrate whenever the row's
  // `updated_at` changes — but never while the user has unsaved edits, so their
  // in-progress input is preserved. (The old "hydrate once" guard latched on
  // the initial empty `[]` and then ignored the real row forever → stale data.)
  const hydratedOnceRef = useRef(false);
  const lastHydratedAtRef = useRef<string | null>(null);

  useEffect(() => {
    if (dirty) return; // don't clobber unsaved edits
    const incomingAt = row?.updated_at ?? null;
    if (hydratedOnceRef.current && lastHydratedAtRef.current === incomingAt) return;

    setOrgName(row?.org_name ?? '');
    setOrgLocalname(row?.org_localname ?? '');
    setLogoUrl(row?.logo_url ?? '');
    setOrgAddress(row?.org_address ?? '');
    setOrgPhone(row?.org_phone ?? '');
    setOrgMobile(row?.org_mobile ?? '');
    setOrgEmail(row?.org_email ?? '');
    setCountry(row?.country ?? 'PH');
    setRegion(row?.region ?? 'Philippines');
    setLanguage(row?.language ?? 'en');
    setCurrency(row?.currency ?? 'PHP');
    setSmsSenderName(row?.sms_sender_name ?? '');
    setRegional(getRegionalSettings(row));

    // PRD.282 — hydrate clinic classification (defaults to general via backfill).
    const cls = getClinicClassification(row);
    setPrimaryClinicType(cls.primaryClinicType);
    setAdditionalSpecialties(cls.additionalSpecialties);

    // PRD.283 — hydrate Invoice module state (defaults to enabled).
    setInvoiceModuleEnabled(isInvoiceModuleEnabled(row));
    setLaboratoryModuleEnabled(isLaboratoryModuleEnabled(row));

    // PRD.284 — hydrate print branding (font color + letterhead free-text).
    setPrintBranding(getPrintBranding(row));

    hydratedOnceRef.current = true;
    lastHydratedAtRef.current = incomingAt;
  }, [row, dirty]);

  // Dropping the primary type's implied specialty keeps the two selections
  // consistent (e.g. switching primary to Dental removes a Dental specialty).
  useEffect(() => {
    const implied = new Set(getImpliedSpecialties(primaryClinicType));
    setAdditionalSpecialties((prev) => {
      const next = prev.filter((s) => !implied.has(s));
      return next.length === prev.length ? prev : next;
    });
  }, [primaryClinicType]);

  const toggleSpecialty = useCallback((code: ClinicSpecialty) => {
    setDirty(true);
    setAdditionalSpecialties((prev) =>
      prev.includes(code) ? prev.filter((s) => s !== code) : [...prev, code],
    );
  }, []);

  const showToast = useCallback((text: string, tone: 'success' | 'error' = 'success') => {
    setToast({ text, tone });
    setTimeout(() => setToast(null), 2800);
  }, []);

  // ─── Validation ───
  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (orgEmail.trim() && !EMAIL_RE.test(orgEmail.trim())) e.orgEmail = 'Invalid email';
    if (logoUrl.trim() && !URL_RE.test(logoUrl.trim())) e.logoUrl = 'Invalid URL';
    if (!regional.default_mobile_country_code.startsWith('+')) e.defaultCc = 'Must start with "+"';
    const dp = regional.money_format.decimal_places;
    if (!Number.isInteger(dp) || dp < 0 || dp > 4) e.decimalPlaces = 'Must be 0–4';
    const codeSet = new Set<string>();
    for (const t of regional.id_types) {
      if (!t.label.trim()) {
        e.idTypes = 'All ID type labels must be non-empty';
        break;
      }
      if (codeSet.has(t.code)) {
        e.idTypes = 'ID type codes must be unique';
        break;
      }
      codeSet.add(t.code);
    }
    if (language !== 'en') e.language = 'Only "en" is supported in MVP';
    return e;
  }, [orgEmail, logoUrl, regional, language]);

  const hasErrors = Object.keys(errors).length > 0;

  // ─── Country / Currency change handlers ───

  // Selecting a country auto-syncs the matching currency (incl. its symbol
  // and conventional decimal places), default mobile country code, and
  // region label. Everything stays editable afterwards, so non-standard
  // setups (e.g. clinic in Spain billing USD) are still supported.
  const handleCountryChange = useCallback((nextCode: string) => {
    setCountry(nextCode);
    const preset = COUNTRY_PRESETS.find((p) => p.code === nextCode);
    if (!preset) return;
    setRegion(preset.region);
    setCurrency(preset.currency);
    const ccyOpt = CURRENCY_OPTIONS.find((c) => c.code === preset.currency);
    setRegional((cur) => ({
      ...cur,
      default_mobile_country_code: preset.mobile_cc,
      money_format: {
        ...cur.money_format,
        currency_symbol: preset.currency_symbol,
        decimal_places: ccyOpt?.decimal_places ?? cur.money_format.decimal_places,
      },
    }));
  }, []);

  // Selecting a currency auto-fills its symbol and ISO-4217 minor unit count.
  const handleCurrencyChange = useCallback((nextCode: string) => {
    setCurrency(nextCode);
    const opt = CURRENCY_OPTIONS.find((c) => c.code === nextCode);
    if (!opt) return;
    setRegional((cur) => ({
      ...cur,
      money_format: {
        ...cur.money_format,
        currency_symbol: opt.symbol,
        decimal_places: opt.decimal_places,
      },
    }));
  }, []);

  const countryHasPreset = useMemo(
    () => COUNTRY_PRESETS.some((p) => p.code === country),
    [country],
  );
  const countryIdPreset = useMemo(() => COUNTRY_ID_TYPES[country] ?? null, [country]);
  const countryLabel = useMemo(
    () => COUNTRY_PRESETS.find((p) => p.code === country)?.label ?? country,
    [country],
  );

  const applyCountryIdDefaults = useCallback(() => {
    const preset = COUNTRY_ID_TYPES[country];
    if (!preset) return;
    const ok =
      regional.id_types.length === 0 ||
      window.confirm(
        `Replace the current ID types with the ${countryLabel} preset? This will discard any custom changes.`,
      );
    if (!ok) return;
    setDirty(true);
    setRegional((cur) => ({ ...cur, id_types: preset.map((t) => ({ ...t })) }));
  }, [country, countryLabel, regional.id_types.length]);
  const currencyHasPreset = useMemo(
    () => CURRENCY_OPTIONS.some((c) => c.code === currency),
    [currency],
  );

  // ─── Money preview ───
  const moneyPreview = useMemo(() => formatMoney(12500, regional), [regional]);

  // ─── ID types editor ───
  const updateIdType = useCallback((index: number, patch: Partial<IdTypeEntry>) => {
    setRegional((cur) => ({
      ...cur,
      id_types: cur.id_types.map((t, i) => (i === index ? { ...t, ...patch } : t)),
    }));
  }, []);

  const removeIdType = useCallback((index: number) => {
    setDirty(true);
    setRegional((cur) => ({
      ...cur,
      id_types: cur.id_types.filter((_, i) => i !== index),
    }));
  }, []);

  const addCustomIdType = useCallback(() => {
    const label = newIdLabel.trim();
    if (!label) return;
    setDirty(true);
    setRegional((cur) => {
      const codes = new Set(cur.id_types.map((t) => t.code));
      const code = uniqueCode(toSnakeCode(label), codes);
      return {
        ...cur,
        id_types: [...cur.id_types, { code, label, enabled: true }],
      };
    });
    setNewIdLabel('');
  }, [newIdLabel]);

  // ─── Save ───
  // Persist the clinic profile (incl. classification in extra_json) and,
  // optionally, apply the workspace preset. Both writes go through the existing
  // paths (app_config db.execute + the shared patient_workspace_settings write).
  const doSave = useCallback(
    async ({ applyWorkspacePreset }: { applyWorkspacePreset: boolean }) => {
      if (!db) return;
      setSaving(true);
      try {
        const existing = await db.getAll<{ extra_json: string | null }>(
          `SELECT extra_json FROM app_config WHERE id = 'app' LIMIT 1`,
        );
        const currentExtra = existing[0]?.extra_json ?? null;
        // Merge both extensible blobs into the same extra_json (order-independent).
        let nextExtra = writeRegionalSettings(currentExtra, regional);
        nextExtra = writeClinicClassification(nextExtra, formClassification);
        // PRD.283 / PRD.305 — replace only the configurable main-module keys;
        // all other extra_json keys (and any other main_modules keys) are
        // preserved by the merge helper.
        nextExtra = writeMainModules(nextExtra, {
          invoice: invoiceModuleEnabled,
          laboratory: laboratoryModuleEnabled,
        });
        // PRD.284 — persist print branding (font color + letterhead free-text).
        nextExtra = writePrintBranding(nextExtra, printBranding);
        const now = new Date().toISOString();
        const updatedBy = userId;

        const exists = existing.length > 0;
        if (exists) {
          await db.execute(
            `UPDATE app_config
               SET org_name = ?, org_localname = ?, logo_url = ?,
                   country = ?, region = ?, language = ?, currency = ?,
                   org_address = ?, org_phone = ?, org_mobile = ?, org_email = ?,
                   sms_sender_name = ?, extra_json = ?, updated_at = ?, updated_by = ?
             WHERE id = 'app'`,
            [
              orgName.trim(),
              orgLocalname.trim() || null,
              logoUrl.trim() || null,
              country.trim() || null,
              region.trim() || null,
              language.trim() || null,
              currency.trim() || null,
              orgAddress.trim() || null,
              orgPhone.trim() || null,
              orgMobile.trim() || null,
              orgEmail.trim() || null,
              smsSenderName.trim() || null,
              nextExtra,
              now,
              updatedBy,
            ],
          );
        } else {
          await db.execute(
            `INSERT INTO app_config (
               id, org_name, org_localname, logo_url, country, region, language, currency,
               org_address, org_phone, org_mobile, org_email, sms_sender_name,
               extra_json, updated_at, updated_by
             ) VALUES ('app', ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            [
              orgName.trim(),
              orgLocalname.trim() || null,
              logoUrl.trim() || null,
              country.trim() || null,
              region.trim() || null,
              language.trim() || null,
              currency.trim() || null,
              orgAddress.trim() || null,
              orgPhone.trim() || null,
              orgMobile.trim() || null,
              orgEmail.trim() || null,
              smsSenderName.trim() || null,
              nextExtra,
              now,
              updatedBy,
            ],
          );
        }

        // PRD.282 §1.4/§1.5 — apply the workspace preset when requested (a new
        // clinic with no workspace config, or an admin who chose Apply Defaults).
        // Non-tab settings on any existing row are preserved.
        if (applyWorkspacePreset) {
          const preset = buildClinicWorkspacePreset(formClassification);
          const existingWs = workspaceRows[0] ?? null;
          const baseValues: Omit<PatientWorkspaceSettingsUI, 'id'> = existingWs
            ? (() => {
                const { id: _omit, ...rest } = mapPatientWorkspaceSettingsToUI(existingWs);
                return rest;
              })()
            : { ...PATIENT_WORKSPACE_DEFAULTS };
          const nextValues = applyPresetToWorkspaceSettings(baseValues, preset);
          await persistPatientWorkspaceSettings(db, {
            orgId,
            existing: existingWs ? { id: existingWs.id } : null,
            values: nextValues,
          });
        }

        // Saved — clear dirty so the form re-syncs with the row (incl. any
        // concurrent updates) on the next watch emission.
        setDirty(false);
        showToast('Clinic settings saved');
      } catch (err) {
        console.error('[ClinicSettings] Save failed:', err);
        // PRD §Error Handling — do not indicate defaults were applied on failure.
        showToast('Save failed — check console for details', 'error');
      } finally {
        setSaving(false);
      }
    },
    [
      db,
      orgId,
      orgName,
      orgLocalname,
      logoUrl,
      country,
      region,
      language,
      currency,
      orgAddress,
      orgPhone,
      orgMobile,
      orgEmail,
      smsSenderName,
      regional,
      userId,
      formClassification,
      invoiceModuleEnabled,
      laboratoryModuleEnabled,
      printBranding,
      workspaceRows,
      showToast,
    ],
  );

  const handleSave = useCallback(async () => {
    if (!db || !isAdmin || saving || hasErrors) return;
    if (!orgName.trim()) {
      showToast('Clinic name is required', 'error');
      return;
    }
    const workspaceExists = workspaceRows.length > 0;
    // Existing clinic + a classification change → confirm before touching the
    // saved workspace settings (PRD §1.5). Defer all writes to the dialog choice.
    if (workspaceExists && classificationChanged) {
      setClinicDefaultsConfirmOpen(true);
      return;
    }
    // New clinic (no workspace config yet) auto-applies the preset; an existing
    // clinic with no classification change saves without touching the workspace.
    await doSave({ applyWorkspacePreset: !workspaceExists });
  }, [
    db,
    isAdmin,
    saving,
    hasErrors,
    orgName,
    workspaceRows,
    classificationChanged,
    doSave,
    showToast,
  ]);

  // ─── Render ───

  const readOnly = !isAdmin;

  return (
    // onChange bubbles from every descendant input/select/checkbox, so any edit
    // marks the form dirty and suspends re-hydration until the user saves.
    <div style={pageStyle} onChange={() => setDirty(true)}>
      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            padding: '10px 20px',
            background: toast.tone === 'error' ? '#dc2626' : '#0f172a',
            color: '#fff',
            borderRadius: 'var(--radius-lg, 14px)',
            fontSize: 'var(--font-size-sm, 14px)',
            fontWeight: 'var(--font-weight-medium, 500)',
            zIndex: 9999,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {toast.text}
        </div>
      )}

      {!isAdmin && (
        <div
          style={{
            padding: '10px 14px',
            background: '#fffbeb',
            border: '1px solid #fde68a',
            color: '#92400e',
            borderRadius: 'var(--radius-md, 10px)',
            fontSize: 'var(--font-size-sm, 14px)',
          }}
        >
          You have read-only access. Only admins can update clinic settings.
        </div>
      )}

      {/* ─── Clinic Profile ─── */}
      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Clinic Profile</h2>

        <div>
          <div style={labelStyle}>Clinic name</div>
          <input
            style={readOnly ? inputReadOnly : inputStyle}
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            readOnly={readOnly}
            placeholder="e.g. Sunrise Family Clinic"
          />
        </div>

        <div>
          <div style={labelStyle}>Local name</div>
          <input
            style={readOnly ? inputReadOnly : inputStyle}
            value={orgLocalname}
            onChange={(e) => setOrgLocalname(e.target.value)}
            readOnly={readOnly}
          />
        </div>

        {/* ─── PRD.282 — Clinic type classification ─── */}
        <div>
          <label style={labelStyle} htmlFor="primary-clinic-type">
            Primary clinic type <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <select
            id="primary-clinic-type"
            style={readOnly ? inputReadOnly : inputStyle}
            value={primaryClinicType}
            onChange={(e) => {
              setDirty(true);
              setPrimaryClinicType(e.target.value as PrimaryClinicType);
            }}
            disabled={readOnly}
          >
            {PRIMARY_CLINIC_TYPES.map((code) => (
              <option key={code} value={code}>
                {PRIMARY_CLINIC_TYPE_LABELS[code]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div style={labelStyle} id="additional-specialties-label">
            Additional specialties
          </div>
          <div
            role="group"
            aria-labelledby="additional-specialties-label"
            style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
          >
            {availableSpecialties.map((code) => {
              const checked = additionalSpecialties.includes(code);
              return (
                <label
                  key={code}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 10px',
                    borderRadius: 'var(--radius-md, 10px)',
                    border: `1px solid ${checked ? 'var(--color-primary, #4f46e5)' : 'var(--border, rgba(15,23,42,0.12))'}`,
                    background: checked ? 'var(--color-primary-soft, #eef2ff)' : '#fff',
                    color: 'var(--text, #0f172a)',
                    fontSize: 'var(--font-size-sm, 14px)',
                    cursor: readOnly ? 'default' : 'pointer',
                    opacity: readOnly ? 0.7 : 1,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={readOnly}
                    onChange={() => toggleSpecialty(code)}
                  />
                  {CLINIC_SPECIALTY_LABELS[code]}
                </label>
              );
            })}
          </div>
        </div>

        <div style={{ fontSize: 12, color: 'var(--text-muted, #64748b)', marginTop: -4 }}>
          Clinic type is used to suggest Patient Workspace defaults. Administrators can customize
          the workspace afterward.
        </div>

        <div>
          <div style={labelStyle}>Logo URL</div>
          <input
            style={readOnly ? inputReadOnly : inputStyle}
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            readOnly={readOnly}
            placeholder="https://…"
          />
          {errors.logoUrl && (
            <div style={{ marginTop: 4, fontSize: 12, color: '#dc2626' }}>{errors.logoUrl}</div>
          )}
        </div>

        <div>
          <div style={labelStyle}>Address</div>
          <input
            style={readOnly ? inputReadOnly : inputStyle}
            value={orgAddress}
            onChange={(e) => setOrgAddress(e.target.value)}
            readOnly={readOnly}
          />
        </div>

        <div style={twoColRow}>
          <div>
            <div style={labelStyle}>Phone</div>
            <input
              style={readOnly ? inputReadOnly : inputStyle}
              value={orgPhone}
              onChange={(e) => setOrgPhone(e.target.value)}
              readOnly={readOnly}
            />
          </div>
          <div>
            <div style={labelStyle}>Mobile</div>
            <input
              style={readOnly ? inputReadOnly : inputStyle}
              value={orgMobile}
              onChange={(e) => setOrgMobile(e.target.value)}
              readOnly={readOnly}
            />
          </div>
        </div>

        <div>
          <div style={labelStyle}>Email</div>
          <input
            style={readOnly ? inputReadOnly : inputStyle}
            value={orgEmail}
            onChange={(e) => setOrgEmail(e.target.value)}
            readOnly={readOnly}
            placeholder="clinic@example.com"
          />
          {errors.orgEmail && (
            <div style={{ marginTop: 4, fontSize: 12, color: '#dc2626' }}>{errors.orgEmail}</div>
          )}
        </div>
      </section>

      {/* ─── PRD.284 — Print Branding (Letterhead Classic) ─── */}
      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Print Branding</h2>
        <div style={{ fontSize: 12, color: 'var(--text-muted, #64748b)', marginTop: -4 }}>
          Used by invoice and prescription printouts. The doctor / registration / footer fields feed
          the <strong>Letterhead Classic</strong> template; empty fields are omitted.
        </div>

        <div>
          <div style={labelStyle}>Print font color</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              type="color"
              aria-label="Print font color"
              value={normalizePrintFontColor(printBranding.printFontColor)}
              onChange={(e) => patchPrintBranding({ printFontColor: e.target.value })}
              disabled={readOnly}
              style={{
                width: 44,
                height: 32,
                padding: 0,
                border: '1px solid var(--border, rgba(15,23,42,0.12))',
                borderRadius: 'var(--radius-md, 10px)',
                background: '#fff',
                cursor: readOnly ? 'default' : 'pointer',
              }}
            />
            <input
              style={{ ...(readOnly ? inputReadOnly : inputStyle), maxWidth: 140 }}
              value={printBranding.printFontColor}
              onChange={(e) => patchPrintBranding({ printFontColor: e.target.value })}
              readOnly={readOnly}
              placeholder="#000000"
            />
          </div>
          <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-muted, #64748b)' }}>
            Applies to printed text and app-generated chrome. Default black (#000000).
          </div>
        </div>

        <div>
          <div style={labelStyle}>Letterhead doctor name</div>
          <input
            style={readOnly ? inputReadOnly : inputStyle}
            value={printBranding.letterheadDoctorName}
            onChange={(e) => patchPrintBranding({ letterheadDoctorName: e.target.value })}
            readOnly={readOnly}
            placeholder="e.g. Dr. M. Shanmugam"
          />
          <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-muted, #64748b)' }}>
            The large name in the letterhead's left column. Leave blank to use the invoice's
            attending provider / the prescribing doctor.
          </div>
        </div>

        <div style={twoColRow}>
          <div>
            <div style={labelStyle}>Doctor qualifications</div>
            <input
              style={readOnly ? inputReadOnly : inputStyle}
              value={printBranding.doctorQualifications}
              onChange={(e) => patchPrintBranding({ doctorQualifications: e.target.value })}
              readOnly={readOnly}
              placeholder="e.g. MBBS, MD"
            />
          </div>
          <div>
            <div style={labelStyle}>Specialty</div>
            <input
              style={readOnly ? inputReadOnly : inputStyle}
              value={printBranding.doctorSpecialty}
              onChange={(e) => patchPrintBranding({ doctorSpecialty: e.target.value })}
              readOnly={readOnly}
              placeholder="e.g. Dermatology"
            />
          </div>
        </div>

        <div>
          <div style={labelStyle}>Designation</div>
          <input
            style={readOnly ? inputReadOnly : inputStyle}
            value={printBranding.doctorDesignation}
            onChange={(e) => patchPrintBranding({ doctorDesignation: e.target.value })}
            readOnly={readOnly}
            placeholder="e.g. Consultant Physician"
          />
        </div>

        <div style={twoColRow}>
          <div>
            <div style={labelStyle}>Registration number</div>
            <input
              style={readOnly ? inputReadOnly : inputStyle}
              value={printBranding.registrationNumber}
              onChange={(e) => patchPrintBranding({ registrationNumber: e.target.value })}
              readOnly={readOnly}
            />
          </div>
          <div>
            <div style={labelStyle}>Additional registrations</div>
            <input
              style={readOnly ? inputReadOnly : inputStyle}
              value={printBranding.additionalRegistrations}
              onChange={(e) => patchPrintBranding({ additionalRegistrations: e.target.value })}
              readOnly={readOnly}
            />
          </div>
        </div>

        <div>
          <div style={labelStyle}>Insurance / Panel information</div>
          <input
            style={readOnly ? inputReadOnly : inputStyle}
            value={printBranding.insurancePanel}
            onChange={(e) => patchPrintBranding({ insurancePanel: e.target.value })}
            readOnly={readOnly}
          />
        </div>

        <div>
          <div style={labelStyle}>Additional footer lines</div>
          <textarea
            style={{
              ...(readOnly ? inputReadOnly : inputStyle),
              minHeight: 64,
              resize: 'vertical',
              fontFamily: 'inherit',
            }}
            value={printBranding.footerExtraText}
            onChange={(e) => patchPrintBranding({ footerExtraText: e.target.value })}
            readOnly={readOnly}
            placeholder="One line per row — shown centered in the footer."
          />
        </div>
      </section>

      {/* ─── PRD.283 — Main Modules ─── */}
      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Main Modules</h2>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted, #64748b)' }}>
          Choose which available modules your clinic currently uses. Disabled modules can be enabled
          again later without losing data. Additional modules will become configurable in a future
          update.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {MAIN_MODULE_ROWS.map((mod) => {
            // PRD.305 — the configurable set is now data-driven rather than an
            // `=== 'invoice'` check, so adding a third module means adding a row
            // above plus its state, not editing this branch again.
            const state = MODULE_STATE[mod.key];
            const editable = mod.configurable && state != null;
            const checked = editable ? state.value : true;
            // Locked modules are always checked and non-interactive. Configurable
            // ones are editable only for admins (readOnly === !isAdmin).
            const controlDisabled = !editable || readOnly;
            return (
              <label
                key={mod.key}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '10px 12px',
                  border: '1px solid var(--border, rgba(15,23,42,0.12))',
                  borderRadius: 'var(--radius-md, 10px)',
                  background: controlDisabled ? '#f8fafc' : '#fff',
                  cursor: controlDisabled ? 'default' : 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={controlDisabled}
                  onChange={(e) => {
                    if (!editable) return;
                    setDirty(true);
                    state.set(e.target.checked);
                  }}
                  style={{ marginTop: 2, width: 16, height: 16 }}
                />
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 'var(--font-weight-medium, 500)',
                      color: 'var(--text, #0f172a)',
                    }}
                  >
                    {mod.label}
                    {!editable && (
                      <span style={{ marginLeft: 8, fontSize: 12, color: '#94a3b8' }}>Locked</span>
                    )}
                  </span>
                  {mod.description && (
                    <span style={{ fontSize: 12, color: 'var(--text-muted, #64748b)' }}>
                      {mod.description}
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      </section>

      {/* ─── Regional Settings ─── */}
      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Regional Settings</h2>

        <div style={twoColRow}>
          <div>
            <div style={labelStyle}>Country</div>
            <select
              style={readOnly ? inputReadOnly : inputStyle}
              value={country}
              onChange={(e) => handleCountryChange(e.target.value)}
              disabled={readOnly}
            >
              {!countryHasPreset && country && <option value={country}>{country} (custom)</option>}
              {COUNTRY_PRESETS.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.label} ({p.code})
                </option>
              ))}
            </select>
          </div>
          <div>
            <div style={labelStyle}>Region</div>
            <input
              style={readOnly ? inputReadOnly : inputStyle}
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              readOnly={readOnly}
            />
          </div>
        </div>

        <div style={twoColRow}>
          <div>
            <div style={labelStyle}>Language</div>
            <select
              style={readOnly ? inputReadOnly : inputStyle}
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              disabled={readOnly}
            >
              <option value="en">English (en)</option>
            </select>
            {errors.language && (
              <div style={{ marginTop: 4, fontSize: 12, color: '#dc2626' }}>{errors.language}</div>
            )}
          </div>
          <div>
            <div style={labelStyle}>Currency</div>
            <select
              style={readOnly ? inputReadOnly : inputStyle}
              value={currency}
              onChange={(e) => handleCurrencyChange(e.target.value)}
              disabled={readOnly}
            >
              {!currencyHasPreset && currency && (
                <option value={currency}>{currency} (custom)</option>
              )}
              {CURRENCY_OPTIONS.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code} — {c.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <div style={labelStyle}>Default mobile country code</div>
          <input
            style={readOnly ? inputReadOnly : { ...inputStyle, maxWidth: 160 }}
            value={regional.default_mobile_country_code}
            onChange={(e) =>
              setRegional((cur) => ({ ...cur, default_mobile_country_code: e.target.value }))
            }
            readOnly={readOnly}
            placeholder="+63"
          />
          {errors.defaultCc && (
            <div style={{ marginTop: 4, fontSize: 12, color: '#dc2626' }}>{errors.defaultCc}</div>
          )}
        </div>

        {/* Money format */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-3, 12px)',
            padding: 'var(--sp-4, 16px)',
            background: '#f8fafc',
            border: '1px solid var(--border, rgba(15,23,42,0.12))',
            borderRadius: 'var(--radius-md, 10px)',
          }}
        >
          <div
            style={{
              fontSize: 'var(--font-size-sm, 14px)',
              fontWeight: 'var(--font-weight-semi, 600)',
              color: 'var(--text, #0f172a)',
            }}
          >
            Money format
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={regional.money_format.use_thousands_separator}
              disabled={readOnly}
              onChange={(e) =>
                setRegional((cur) => ({
                  ...cur,
                  money_format: {
                    ...cur.money_format,
                    use_thousands_separator: e.target.checked,
                  },
                }))
              }
            />
            <span style={{ fontSize: 'var(--font-size-sm, 14px)' }}>Use thousands separator</span>
          </label>

          <div style={twoColRow}>
            <div>
              <div style={labelStyle}>Thousands separator</div>
              <input
                style={readOnly ? inputReadOnly : inputStyle}
                value={regional.money_format.thousands_separator}
                onChange={(e) =>
                  setRegional((cur) => ({
                    ...cur,
                    money_format: { ...cur.money_format, thousands_separator: e.target.value },
                  }))
                }
                readOnly={readOnly}
                maxLength={2}
              />
            </div>
            <div>
              <div style={labelStyle}>Decimal separator</div>
              <input
                style={readOnly ? inputReadOnly : inputStyle}
                value={regional.money_format.decimal_separator}
                onChange={(e) =>
                  setRegional((cur) => ({
                    ...cur,
                    money_format: { ...cur.money_format, decimal_separator: e.target.value },
                  }))
                }
                readOnly={readOnly}
                maxLength={2}
              />
            </div>
          </div>

          <div style={twoColRow}>
            <div>
              <div style={labelStyle}>Decimal places</div>
              <input
                type="number"
                min={0}
                max={4}
                style={readOnly ? inputReadOnly : inputStyle}
                value={regional.money_format.decimal_places}
                onChange={(e) =>
                  setRegional((cur) => ({
                    ...cur,
                    money_format: {
                      ...cur.money_format,
                      decimal_places: Number(e.target.value),
                    },
                  }))
                }
                readOnly={readOnly}
              />
              {errors.decimalPlaces && (
                <div style={{ marginTop: 4, fontSize: 12, color: '#dc2626' }}>
                  {errors.decimalPlaces}
                </div>
              )}
            </div>
            <div>
              <div style={labelStyle}>Currency display</div>
              <select
                style={readOnly ? inputReadOnly : inputStyle}
                value={regional.money_format.currency_display}
                onChange={(e) =>
                  setRegional((cur) => ({
                    ...cur,
                    money_format: {
                      ...cur.money_format,
                      currency_display: e.target.value as MoneyCurrencyDisplay,
                    },
                  }))
                }
                disabled={readOnly}
              >
                <option value="symbol">
                  Symbol ({regional.money_format.currency_symbol || '—'})
                </option>
                <option value="code">Code ({currency || '—'})</option>
              </select>
            </div>
          </div>

          <div style={twoColRow}>
            <div>
              <div style={labelStyle}>Currency symbol</div>
              <input
                style={readOnly ? inputReadOnly : inputStyle}
                value={regional.money_format.currency_symbol}
                onChange={(e) =>
                  setRegional((cur) => ({
                    ...cur,
                    money_format: { ...cur.money_format, currency_symbol: e.target.value },
                  }))
                }
                readOnly={readOnly}
                maxLength={4}
              />
            </div>
            <div>
              <div style={labelStyle}>Currency position</div>
              <select
                style={readOnly ? inputReadOnly : inputStyle}
                value={regional.money_format.currency_position}
                onChange={(e) =>
                  setRegional((cur) => ({
                    ...cur,
                    money_format: {
                      ...cur.money_format,
                      currency_position: e.target.value as MoneyCurrencyPosition,
                    },
                  }))
                }
                disabled={readOnly}
              >
                <option value="prefix">
                  Prefix (
                  {formatMoney(12500, {
                    ...regional,
                    money_format: { ...regional.money_format, currency_position: 'prefix' },
                  })}
                  )
                </option>
                <option value="suffix">
                  Suffix (
                  {formatMoney(12500, {
                    ...regional,
                    money_format: { ...regional.money_format, currency_position: 'suffix' },
                  })}
                  )
                </option>
              </select>
            </div>
          </div>

          <div
            style={{
              marginTop: 4,
              padding: '10px 14px',
              background: '#fff',
              border: '1px dashed var(--border, rgba(15,23,42,0.12))',
              borderRadius: 'var(--radius-md, 10px)',
              fontSize: 'var(--font-size-sm, 14px)',
            }}
          >
            <span style={{ color: 'var(--text-muted, #64748b)' }}>Preview: </span>
            <strong>{moneyPreview}</strong>
          </div>
        </div>

        {/* ID types editor */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-3, 12px)',
            padding: 'var(--sp-4, 16px)',
            background: '#f8fafc',
            border: '1px solid var(--border, rgba(15,23,42,0.12))',
            borderRadius: 'var(--radius-md, 10px)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--sp-3, 12px)',
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                fontSize: 'var(--font-size-sm, 14px)',
                fontWeight: 'var(--font-weight-semi, 600)',
                color: 'var(--text, #0f172a)',
              }}
            >
              ID types
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3, 12px)' }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted, #64748b)' }}>
                Disable rather than delete when possible.
              </span>
              {!readOnly && countryIdPreset && (
                <button
                  type="button"
                  style={{ ...btnStyle, padding: '4px 10px', fontSize: 12 }}
                  onClick={applyCountryIdDefaults}
                  title={`Replace ID types with ${countryLabel} preset`}
                >
                  Apply {countryLabel} defaults
                </button>
              )}
            </div>
          </div>

          {regional.id_types.map((t, i) => (
            <div key={t.code} style={idTypeRowStyle}>
              <input
                type="checkbox"
                checked={t.enabled}
                disabled={readOnly}
                onChange={(e) => updateIdType(i, { enabled: e.target.checked })}
              />
              <input
                style={{ ...inputStyle, flex: 1 }}
                value={t.label}
                onChange={(e) => updateIdType(i, { label: e.target.value })}
                readOnly={readOnly}
              />
              <code
                style={{
                  fontSize: 12,
                  color: 'var(--text-muted, #64748b)',
                  minWidth: 120,
                  textAlign: 'right',
                }}
              >
                {t.code}
              </code>
              {!readOnly && (
                <button
                  type="button"
                  style={btnDangerLinkStyle}
                  onClick={() => removeIdType(i)}
                  title="Remove (prefer disabling)"
                >
                  Remove
                </button>
              )}
            </div>
          ))}

          {errors.idTypes && <div style={{ fontSize: 12, color: '#dc2626' }}>{errors.idTypes}</div>}

          {!readOnly && (
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <input
                style={{ ...inputStyle, flex: 1 }}
                placeholder="Add custom ID type (e.g. Company ID)"
                value={newIdLabel}
                onChange={(e) => setNewIdLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCustomIdType();
                  }
                }}
              />
              <button type="button" style={btnStyle} onClick={addCustomIdType}>
                Add
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ─── Messaging ─── */}
      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Messaging</h2>
        <div>
          <div style={labelStyle}>SMS sender name</div>
          <input
            style={readOnly ? inputReadOnly : inputStyle}
            value={smsSenderName}
            onChange={(e) => setSmsSenderName(e.target.value)}
            readOnly={readOnly}
            placeholder="e.g. SunriseClinic"
            maxLength={11}
          />
          <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-muted, #64748b)' }}>
            Used as the alphanumeric sender ID for outbound SMS where supported.
          </div>
        </div>
      </section>

      {/* ─── Save bar ─── */}
      {isAdmin && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            style={{
              ...btnPrimaryStyle,
              opacity: saving || hasErrors ? 0.6 : 1,
              cursor: saving || hasErrors ? 'not-allowed' : 'pointer',
            }}
            onClick={handleSave}
            disabled={saving || hasErrors}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      )}

      {/* PRD.282 §1.5 — confirm before replacing an existing clinic's workspace
          settings when the clinic type/specialties change. */}
      <Modal
        open={clinicDefaultsConfirmOpen}
        onOpenChange={setClinicDefaultsConfirmOpen}
        title="Apply clinic workspace defaults?"
        description="This will update the Patient Sidebar and Visit Note tab selections using the selected clinic type and specialties. You can customize them afterward."
        maxWidth={480}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
            {/* Cancel — saves neither the classification nor the workspace. */}
            <Button variant="ghost" size="md" onClick={() => setClinicDefaultsConfirmOpen(false)}>
              Cancel
            </Button>
            {/* Keep Current Settings — saves classification, leaves workspace as-is. */}
            <Button
              variant="outline"
              size="md"
              onClick={() => {
                setClinicDefaultsConfirmOpen(false);
                void doSave({ applyWorkspacePreset: false });
              }}
            >
              Keep Current Settings
            </Button>
            {/* Apply Defaults — saves classification and replaces the tab selections. */}
            <Button
              variant="gradient"
              size="md"
              onClick={() => {
                setClinicDefaultsConfirmOpen(false);
                void doSave({ applyWorkspacePreset: true });
              }}
            >
              Apply Defaults
            </Button>
          </div>
        }
      >
        {null}
      </Modal>
    </div>
  );
}
